/**
 * LinaAssistant
 *
 * Global wrapper that mounts the Lina AI assistant ONCE at the app shell level
 * so she persists across every tab (previously she only existed inside VideoPage
 * and vanished everywhere else).
 *
 * - Renders a floating "Lina AI" launcher button (bottom-right) when closed.
 * - Renders <VideoAssistantPanel> as a floating popup when open.
 * - Re-openable from Settings → Help via the global `open-lina` event.
 * - When Lina parses a video config she can't apply it directly (she's no longer
 *   a child of VideoPage), so she navigates to the Video tab and broadcasts the
 *   config via the `apply-video-config` event, which VideoPage listens for.
 */

import { useCallback, useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { VideoAssistantPanel } from './VideoAssistantPanel';
import { useTranslation } from '../../hooks/useTranslation';
import type { VideoConfig } from '../../types';

export function LinaAssistant() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Allow re-opening from anywhere (Settings → Help, etc.)
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('open-lina', handler);
    return () => window.removeEventListener('open-lina', handler);
  }, []);

  const handleClose = useCallback(() => setIsOpen(false), []);

  // Lina lives outside VideoPage now. App listens for `apply-video-config`,
  // routes to the Video tab, and forwards the config to the form.
  const handleConfigUpdate = useCallback((config: Partial<VideoConfig>) => {
    window.dispatchEvent(new CustomEvent('apply-video-config', { detail: config }));
  }, []);

  return (
    <>
      <VideoAssistantPanel
        isOpen={isOpen}
        onClose={handleClose}
        onConfigUpdate={handleConfigUpdate}
      />

      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-primary-600 hover:bg-primary-500 text-white shadow-lg shadow-primary-900/30 transition-all hover:scale-105 min-h-[44px]"
          data-testid="video-assistant-button"
          title={t('videoAssistant.openButton', { defaultValue: 'Lina AI' })}
        >
          <Sparkles className="h-5 w-5" />
          <span className="text-sm font-medium hidden sm:inline">
            {t('videoAssistant.openButton', { defaultValue: 'Lina AI' })}
          </span>
        </button>
      )}
    </>
  );
}
